import React, { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Loader2, ChevronDown } from "lucide-react"
import { Input } from "src/components/ui/input"
import { Textarea } from "src/components/ui/textarea"
import { Button } from "src/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "src/components/ui/dropdown-menu"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select"
import { Badge } from "src/components/ui/badge"
import { FormItem, FormLabel, FormControl, FormMessage } from "src/components/ui/form"
import { useLeadStatuses } from "src/features/leads/hooks/useLeadStatuses"
import { fetchMembers } from "src/features/members/api/membersApi"
import { fetchChannelPartnersForPicker } from "src/features/channel_partners/api/channelPartnersApi"

const INDIAN_PHONE_RE = /^[6-9]\d{9}$/

const leadSchema = z
  .object({
    name:           z.string().min(1, "Name is required").max(100, "Max 100 characters"),
    phone:          z.string().regex(INDIAN_PHONE_RE, "Valid 10-digit Indian mobile number").or(z.literal("")).optional(),
    email:          z.string().email("Valid email required").or(z.literal("")).optional(),
    status:         z.string().default("new"),
    source:         z.enum(["channel_partner", "direct", "referral", "campaign", "walk_in", "qr_campaign"]).optional().or(z.literal("")),
    assigned_to_id: z.number().nullable().optional(),
    channel_partner_id:         z.number().nullable().optional(),
  })
  .refine(
    (d) => d.source !== "channel_partner" || !!d.channel_partner_id,
    { message: "Select the channel partner", path: ["channel_partner_id"] }
  )
  .refine(
    (d) => (d.phone && d.phone.length > 0) || (d.email && d.email.length > 0),
    { message: "At least one of phone or email is required", path: ["phone"] }
  )

const SOURCES = [
  { value: "channel_partner", label: "Channel Partner" },
  { value: "direct",          label: "Direct" },
  { value: "referral",        label: "Referral" },
  { value: "campaign",        label: "Campaign" },
  { value: "walk_in",         label: "Walk-in" },
  { value: "qr_campaign",     label: "QR Campaign" },
]

export function LeadForm({ lead, onSubmit, onCancel, isPending }) {
  const { statuses: STATUSES } = useLeadStatuses()
  const [members, setMembers] = useState([])
  const [membersLoaded, setMembersLoaded] = useState(false)
  const [channelPartners, setChannelPartners] = useState([])

  // Load the org's members once so the owner can be picked. `assigned_to_id`
  // is a user id (Lead#assigned_to is a User), so we key on member.user.id.
  useEffect(() => {
    let active = true
    fetchMembers({ per_page: 100 })
      .then((d) => { if (active) setMembers(d.memberships || []) })
      .catch(() => { if (active) setMembers([]) })
      .finally(() => { if (active) setMembersLoaded(true) })
    return () => { active = false }
  }, [])

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(leadSchema),
    defaultValues: {
      name:   "",
      phone:  "",
      email:  "",
      status: "new",
      source: "",
      channel_partner_id: null,
    },
  })

  // Populate form when editing
  useEffect(() => {
    if (lead) {
      reset({
        name:           lead.name   || "",
        phone:          lead.phone  || "",
        email:          lead.email  || "",
        status:         lead.status || "new",
        source:         lead.source || "",
        assigned_to_id: lead.assigned_to?.id ?? null,
        channel_partner_id:         lead.channel_partner_id ?? null,
      })
    } else {
      reset({ name: "", phone: "", email: "", status: "new", source: "", channel_partner_id: null })
    }
  }, [lead, reset])

  const status = watch("status")
  const source = watch("source")
  const assignedToId = watch("assigned_to_id")
  const channelPartnerId = watch("channel_partner_id")

  // Load active channel partners when the source is CP.
  useEffect(() => {
    if (source === "channel_partner" && channelPartners.length === 0) {
      fetchChannelPartnersForPicker().then(setChannelPartners).catch(() => setChannelPartners([]))
    }
  }, [source])

  const selectedCp = channelPartners.find((c) => String(c.id) === String(channelPartnerId))

  const ownerName = (() => {
    if (assignedToId == null) return null
    const m = members.find((x) => x.user?.id === assignedToId)
    return m?.user?.name || m?.user?.email || lead?.assigned_to?.full_name || "Member"
  })()

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 px-6 py-5 overflow-y-auto flex-1">

      {/* Name */}
      <FormItem>
        <FormLabel>Name <span className="text-red-500">*</span></FormLabel>
        <FormControl>
          <Input placeholder="e.g. Amit Sharma" {...register("name")} />
        </FormControl>
        <FormMessage>{errors.name?.message}</FormMessage>
      </FormItem>

      {/* Phone */}
      <FormItem>
        <FormLabel>Phone</FormLabel>
        <FormControl>
          <Input type="tel" placeholder="9876543210" maxLength={10} {...register("phone")} />
        </FormControl>
        <FormMessage>{errors.phone?.message}</FormMessage>
      </FormItem>

      {/* Email */}
      <FormItem>
        <FormLabel>Email</FormLabel>
        <FormControl>
          <Input type="email" placeholder="amit@example.com" {...register("email")} />
        </FormControl>
        <FormMessage>{errors.email?.message}</FormMessage>
        <p className="text-[11px] text-slate-400 -mt-1">At least one of phone or email is required</p>
      </FormItem>

      {/* Status */}
      <FormItem>
        <FormLabel>Status <span className="text-red-500">*</span></FormLabel>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full justify-between font-normal text-slate-700">
              {status ? STATUSES.find((s) => s.value === status)?.label : "Select status"}
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-[240px]">
            <DropdownMenuRadioGroup value={status} onValueChange={(v) => setValue("status", v, { shouldValidate: true })}>
              {STATUSES.map((s) => (
                <DropdownMenuRadioItem key={s.value} value={s.value}>{s.label}</DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <FormMessage>{errors.status?.message}</FormMessage>
      </FormItem>

      {/* Source */}
      <FormItem>
        <FormLabel>Source</FormLabel>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full justify-between font-normal text-slate-700">
              {source ? SOURCES.find((s) => s.value === source)?.label : "Select source"}
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-[240px]">
            <DropdownMenuRadioGroup value={source || "none"} onValueChange={(v) => setValue("source", v === "none" ? "" : v, { shouldValidate: true })}>
              <DropdownMenuRadioItem value="none">— None —</DropdownMenuRadioItem>
              {SOURCES.map((s) => (
                <DropdownMenuRadioItem key={s.value} value={s.value}>{s.label}</DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <FormMessage>{errors.source?.message}</FormMessage>
      </FormItem>

      {/* Channel partner attribution — only when source is channel_partner */}
      {source === "channel_partner" && (
        <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/50 p-3">
          <FormItem>
            <FormLabel>Channel partner</FormLabel>
            <Select
              value={channelPartnerId ? String(channelPartnerId) : ""}
              onValueChange={(v) => {
                setValue("channel_partner_id", Number(v), { shouldValidate: true })
              }}
            >
              <SelectTrigger><SelectValue placeholder="Select partner" /></SelectTrigger>
              <SelectContent>
                {channelPartners.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage>{errors.channel_partner_id?.message}</FormMessage>
          </FormItem>

          {selectedCp && selectedCp.referral_code && (
            <div className="flex items-center gap-2 text-[12px] text-slate-500">
              Attributed code:
              <Badge className="border-0 bg-[#ecebff] font-mono text-[#4f39f6]">{selectedCp.referral_code}</Badge>
            </div>
          )}
        </div>
      )}

      {/* Owner — the user who owns this lead (Lead#assigned_to) */}
      <FormItem>
        <FormLabel>Owner</FormLabel>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full justify-between font-normal text-slate-700">
              {ownerName || "Unassigned"}
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-72 w-[var(--radix-dropdown-menu-trigger-width)] min-w-[240px] overflow-y-auto">
            {!membersLoaded ? (
              <div className="flex items-center gap-2 px-2 py-3 text-[13px] text-slate-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading members…
              </div>
            ) : (
              <DropdownMenuRadioGroup
                value={assignedToId != null ? String(assignedToId) : "none"}
                onValueChange={(v) => setValue("assigned_to_id", v === "none" ? null : Number(v), { shouldValidate: true })}
              >
                <DropdownMenuRadioItem value="none">— Unassigned —</DropdownMenuRadioItem>
                {members.map((m) => (
                  <DropdownMenuRadioItem key={m.id} value={String(m.user?.id)}>
                    {m.user?.name || m.user?.email}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <FormMessage>{errors.assigned_to_id?.message}</FormMessage>
      </FormItem>

      {/* Footer buttons — inside form so Enter submits */}
      <div className="flex justify-end gap-2 pt-2 mt-auto border-t border-slate-100">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={isPending}>
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {lead ? "Save Changes" : "Create Lead"}
        </Button>
      </div>
    </form>
  )
}
